import { compactClassName } from '@/components/homepage-october/compact'
import * as React from 'react'

import { cn } from '@/lib/utils'

// The brand logo stands in for a photo that fails to load (the same fallback the live cards use).
export const FALLBACK = '/Fridge.com-brand-logos/Fridge.com-blue.png'

type Props = Omit<React.ComponentProps<'img'>, 'src'> & { src: string; fit?: 'contain' | 'cover'; eager?: boolean }

// An absent data-state means loading; loaded/error lives on the element, not in React: the page shows about 370 photos, and a
// re-render per photo was measurable main-thread work on a phone. One listener on the document (watchImages) moves
// every photo from loading to loaded or error, including photos in sections React has not drawn yet.
function settle(el: HTMLImageElement) {
  if (!el.complete) return
  if (el.naturalWidth > 0) {
    if (el.dataset.state !== 'error') el.dataset.state = 'loaded'
  } else fail(el)
}
function fail(el: HTMLImageElement) {
  if (el.dataset.state === 'error') return
  el.dataset.state = 'error'
  el.src = FALLBACK
}

/** Call once, before hydrating. Load and error events do not bubble, so this listens in the capture phase. */
export function watchImages() {
  const pick = (e: Event) => (e.target instanceof HTMLImageElement && e.target.classList.contains('v3-img') ? e.target : null)
  const onLoad = (e: Event) => { const t = pick(e); if (t) settle(t) }
  const onError = (e: Event) => { const t = pick(e); if (t) fail(t) }
  document.addEventListener('load', onLoad, true)
  document.addEventListener('error', onError, true)
  // Photos that finished before this script ran.
  document.querySelectorAll<HTMLImageElement>('img.v3-img').forEach(settle)
  return () => { document.removeEventListener('load', onLoad, true); document.removeEventListener('error', onError, true) }
}

/** A product or lifestyle photo with a shimmer while it loads and the brand logo if it fails. */
export function Img({ src, alt = '', fit = 'contain', eager, className, ...rest }: Props) {
  return (
    <img
      src={src}
      alt={alt}
      loading={eager ? 'eager' : 'lazy'}
      decoding="async"
      className={compactClassName(cn('v3-img', fit === 'cover' ? 'object-cover' : 'object-contain', className))}
      {...rest}
    />
  )
}
